/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import '../../styles';

/**
 * @prop {String} label - Defines the descriptive label of what the value relates to. (Optional)
 * @prop {String} value - Defines the value that needs to be highlighted.
 * @prop {String} icon - Defines the icon that the value relates to. (Optional)
 * @prop {String} status - Defines the type of status badge that will be displayed. Accepted values are error, warning, success.
 * @prop {String} size - Default l. Defines the size of the value indicator. Available in two sizes - s and l.
 * @slot footer - Takes Icons and Text as children. (Optional)
 */
export class SHValueIndicator extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      value: { type: String, reflect: true },
      icon: { type: String, reflect: true },
      status: { type: String, reflect: true },
      size: { type: String, reflect: true },
    };
  }

  static get styles() {
    return [
      css`
        :host {
          display: flex;
          flex-direction: column;
        }
        :host > * + * {
          margin-top: 0.25rem;
        }
        .value-wrapper {
          display: flex;
          align-items: center;
        }
        .status {
          margin-left: 0.25rem;
        }
        :host([size='s']) .icon {
          margin-right: 0.25rem;
        }
        :host([size='l']) .icon {
          margin-right: 0.5rem;
        }
        sh-text {
          width: auto;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        /* size */
        :host([size='l']) {
          align-items: center;
          padding: 0.5rem;
        }
        :host([size='s']) {
          align-items: left;
        }
        .value,
        .label {
          font: var(--body-1);
        }
        :host([size='l']) .value {
          line-height: 4rem;
        }
        :host([size='l']) .label {
          font-size: 1rem;
          line-height: 1.5rem;
        }
        :host([size='s']) .label {
          line-height: 1rem;
        }
        :host([size='l']) .value {
          font-size: 3.5rem;
        }
        :host([size='l']) .icon {
          --sh-icon-size-m: 4rem;
          --sh-icon-font-size-m: 4rem;
        }
        :host([size='s']) .icon {
          --sh-icon-size-m: 2rem;
          --sh-icon-font-size-m: 2rem;
        }
        :host([size='s']) .value {
          line-height: 2rem;
        }
        :host([size='s']) .value {
          font-size: 1.75rem;
        }
        slot {
          display: flex;
        }
      `,
    ];
  }

  render() {
    return html`
      <div class="value-wrapper">
        ${this.icon ? html` <sh-icon icon="${this.icon}" class="icon"></sh-icon> ` : ''}
        ${this.value ? html` <sh-text class="value">${this.value}</sh-text> ` : ''}
        ${this.status ? html` <sh-icon icon="${this.status}" class="status"></sh-icon> ` : ''}
      </div>
      ${this.label ? html` <sh-text class="label">${this.label}</sh-text> ` : ''}
      <slot name="footer"></slot>
    `;
  }

  constructor() {
    super();
    this.size = 'l';
  }
}

customElements.define('sh-value-indicator', SHValueIndicator);
